import ActionTypes from './types'
import * as queries from '../lib/queries'
import {listBuildingStaffForBuilding} from '../lib/queries'
import {CACHE_ActiveBuildingId} from '../constants/AppConstants'

/**
 * Loads residents, units, occupancies, and staff from sister buildings.
 * This data is stored separately from the main building data and is used
 * specifically for amenities and events features.
 *
 * @param {Dispatch} dispatch
 * @param {Array<string>} sisterBuildingIds - Array of sister building IDs to load
 * @param {string} activeBuildingId - The active building ID (to check for stale requests)
 * @returns {Promise<void>}
 */
export async function loadSisterBuildingData(dispatch, sisterBuildingIds, activeBuildingId) {
  // Filter out any null/undefined/empty IDs
  const validIds = sisterBuildingIds.filter(id => id && id !== activeBuildingId)

  if (validIds.length === 0) {
    // No sister buildings to load, dispatch empty data
    dispatch({
      type: ActionTypes.SISTER_BUILDING_DATA_LOAD_ALL,
      payload: {
        residents: [],
        units: [],
        occupancies: [],
        staff: [],
      },
    })
    return
  }

  // Load data from all sister buildings in parallel
  const dataPromises = validIds.map(buildingId => queries.loadUnitsAndResidentsAndOnboardingOccupanciesForBuilding(buildingId))
  const staffPromises = validIds.map(buildingId => listBuildingStaffForBuilding(buildingId))

  const [allBuildingsData, allStaffData] = await Promise.all([Promise.all(dataPromises), Promise.all(staffPromises)])

  // Check if we switched buildings while loading
  if (activeBuildingId !== localStorage[CACHE_ActiveBuildingId]) {
    return
  }

  // Merge data from all sister buildings
  let units = []
  let residents = []
  let occupancies = []
  allBuildingsData.forEach(([buildingUnits, buildingResidents, buildingOccupancies]) => {
    units = units.concat(buildingUnits)
    residents = residents.concat(buildingResidents)
    occupancies = occupancies.concat(buildingOccupancies)
  })

  // Flatten and deduplicate staff from all sister buildings
  const staffMap = new Map()
  allStaffData.flat().forEach(staffEntry => {
    const existing = staffMap.get(staffEntry.staffId)
    if (!existing) {
      staffMap.set(staffEntry.staffId, staffEntry)
    }
  })
  const staff = Array.from(staffMap.values())

  dispatch({
    type: ActionTypes.SISTER_BUILDING_DATA_LOAD_ALL,
    payload: {
      residents,
      units,
      occupancies,
      staff,
    },
  })
}

/**
 * Clears sister building data from the store.
 * Typically called when switching buildings.
 *
 * @param {Dispatch} dispatch
 */
export function clearSisterBuildingData(dispatch) {
  dispatch({
    type: ActionTypes.SISTER_BUILDING_DATA_CLEAR,
  })
}
